import { Controller } from "@hotwired/stimulus"

/**
 * AI Chat List Rail Controller — CONEK-3364 Wave 4.
 *
 * Owns the client-side interactivity of AI::Companion::ChatListRailComponent:
 *  - Expand / collapse the rail (toggles which header is visible + width).
 *  - Switch between "active" / "archived" status filters.
 *  - Debounce search input (300ms) before announcing to the companion.
 *  - Forward row selection / new-chat to the outer ai--companion controller
 *    via dispatched custom events.
 *
 * Server-side rendering owns the chat row markup. Mutations (rename,
 * archive, delete) go through the AIChatChannel by way of the parent
 * ai--companion controller — this rail dispatches DOM events that the
 * parent listens for.
 *
 * Events dispatched from this controller:
 *   ai--chat-list-rail:new-chat      detail: {}
 *   ai--chat-list-rail:select-chat   detail: {chatId}
 *   ai--chat-list-rail:search        detail: {query}
 *   ai--chat-list-rail:filter        detail: {status}
 */
export default class extends Controller {
  static targets = [
    // Dimming layer behind the expanded drawer. Purely an affordance — the
    // dismiss behaviour it advertises lives in #maybeCollapseOnOutsideClick,
    // which treats a click on the scrim as an outside click already.
    "scrim",
    "root",
    "overlay",
    "collapsedHeader",
    "expandedHeader",
    "searchBlock",
    "searchInput",
    "list",
    "chatName",
    "renameInput",
    "deleteDialog",
    "deleteConfirmBtn",
    // Docked-only "Show more" at the end of the list; absent everywhere else.
    "loadMoreButton",
  ]

  static values = {
    collapsed: { type: Boolean, default: false },
    status: { type: String, default: "active" },
    // Workstream 3 sub-phase 3 — carried on frame reloads alongside status
    // (see #reloadFrame) so server-side search survives the round-trip.
    search: { type: String, default: "" },
    // Mirror RAIL_EXPANDED_WIDTH / RAIL_COLLAPSED_WIDTH in
    // chat_list_rail_component.rb. Defaults match the Ruby constants so
    // the controller still works if the data attributes are missing.
    expandedWidth: { type: Number, default: 250 },
    collapsedWidth: { type: Number, default: 48 },
    // Server-side row cap the rail was rendered with. Mirrors
    // AIPack::COMPANION_CHATS_LIMIT (50) like expandedWidth/collapsedWidth
    // mirror their Ruby constants; loadMore() bumps it by LOAD_MORE_STEP and
    // reloads the frame, and the server clamps whatever arrives
    // (CompanionLayoutHelper#companion_chat_limit).
    limit: { type: Number, default: 50 },
    // "overlay" (the 420px companion panel) or "docked" (the full-page chat).
    // Mirrors ChatListRailComponent::VARIANTS. A docked rail is a permanent
    // left column: it has no collapsed state, so every collapse path below
    // early-returns rather than being conditionally wired up — one guard in
    // `collapse()` covers Escape, outside-click, companion:opened, selectChat
    // and newChat at once.
    variant: { type: String, default: "overlay" },
  }

  // How much each "Show more" click widens the server-rendered window. The
  // ceiling is the server's (COMPANION_CHAT_LIMIT_MAX) — this only asks.
  static LOAD_MORE_STEP = 50

  get docked() {
    return this.variantValue === "docked"
  }

  // Docked-only. Reloads the frame with a bumped companion_limit rather than
  // appending a page: the frame swap is the machinery every other rail
  // mutation already uses, and the server re-renders the WHOLE window so the
  // active highlight, dividers and empty states stay consistent for free.
  loadMore() {
    this.limitValue += this.constructor.LOAD_MORE_STEP
    // The click discards this button with the frame swap; disable it for the
    // in-flight moment so a double-click doesn't skip a window.
    if (this.hasLoadMoreButtonTarget) this.loadMoreButtonTarget.disabled = true
    this.#reloadFrame()
  }

  connect() {
    this.searchTimeoutId = null
    this.applyCollapsedState()
    // Auto-collapse only when the companion is (re)opened from the
    // header button / shortcut — NOT on every turbo-frame refresh. The
    // ai--companion-sidebar controller fires this when openValue flips
    // to true. Between opens, the user's expand/collapse choice
    // persists via the companion_rail_collapsed cookie (read on render).
    this._handleCompanionOpened = () => this.collapse()
    window.addEventListener("companion:opened", this._handleCompanionOpened)
    // When a draft chat is lazily persisted on its first message, the
    // companion controller fires this so the rail refreshes and the new
    // thread shows up (selected via the companion_current_chat_id cookie).
    this._handleChatCreated = () => this.#reloadFrame()
    window.addEventListener("companion:chat-created", this._handleChatCreated)
    // CONEK-4760 — when expanded, the rail floats as an overlay over the
    // conversation, so a click anywhere outside it collapses it back. Capture
    // phase + contains() works regardless of stopPropagation downstream.
    this._handleOutsidePointerDown = (event) =>
      this.#maybeCollapseOnOutsideClick(event)
    window.addEventListener("pointerdown", this._handleOutsidePointerDown, true)
    // CONEK-4760 — Esc collapses the expanded overlay (WCAG 2.1 SC 1.4.13).
    this._handleEscKeydown = (event) => this.#maybeCollapseOnEscape(event)
    window.addEventListener("keydown", this._handleEscKeydown)
  }

  disconnect() {
    if (this.searchTimeoutId) {
      clearTimeout(this.searchTimeoutId)
      this.searchTimeoutId = null
    }
    if (this._handleCompanionOpened) {
      window.removeEventListener(
        "companion:opened",
        this._handleCompanionOpened,
      )
      this._handleCompanionOpened = null
    }
    if (this._handleChatCreated) {
      window.removeEventListener(
        "companion:chat-created",
        this._handleChatCreated,
      )
      this._handleChatCreated = null
    }
    if (this._handleOutsidePointerDown) {
      window.removeEventListener(
        "pointerdown",
        this._handleOutsidePointerDown,
        true,
      )
      this._handleOutsidePointerDown = null
    }
    if (this._handleEscKeydown) {
      window.removeEventListener("keydown", this._handleEscKeydown)
      this._handleEscKeydown = null
    }
  }

  // Collapse the overlay on Esc. No-op while collapsed. An open delete dialog
  // gets first claim on Esc (close it instead of collapsing).
  #maybeCollapseOnEscape(event) {
    if (event.key !== "Escape") return
    if (this.collapsedValue) return
    if (this.hasDeleteDialogTarget && this.deleteDialogTarget.open) return
    this.collapse()
  }

  // Collapse the overlay when the user clicks outside it. No-op while already
  // collapsed, while the delete-confirm dialog is open, or when clicking the
  // companion resize handle (dragging to resize must not close the list).
  #maybeCollapseOnOutsideClick(event) {
    if (this.collapsedValue) return
    if (this.hasDeleteDialogTarget && this.deleteDialogTarget.open) return
    const target = event.target
    if (!(target instanceof Node)) return
    if (this.hasOverlayTarget && this.overlayTarget.contains(target)) return
    if (
      target instanceof Element &&
      target.closest('[data-ai--companion-sidebar-target="resizeHandle"]')
    ) {
      return
    }
    this.collapse()
  }

  // ──────────────────────────────────────────────────────────────────────
  // Collapse / expand

  // The single choke point for every collapse path (Escape, outside click,
  // companion:opened, selectChat, newChat). A docked rail has nowhere to
  // collapse to, and must NOT write the cookie either: the panel and the
  // full page share `companion_rail_collapsed`, so a docked no-op that still
  // persisted `true` would silently collapse the panel's rail next time.
  collapse() {
    if (this.docked) return
    this.collapsedValue = true
    this.#persistCollapsed(true)
  }

  expand() {
    if (this.docked) return
    this.collapsedValue = false
    this.#persistCollapsed(false)
  }

  // Persist the rail's collapse choice so it survives turbo-frame
  // refreshes (rename/archive/delete/select re-render the rail from the
  // server, which reads this cookie to set initially_collapsed).
  #persistCollapsed(collapsed) {
    document.cookie = `companion_rail_collapsed=${collapsed}; path=/; SameSite=Lax`
  }

  collapsedValueChanged() {
    this.applyCollapsedState()
  }

  applyCollapsedState() {
    if (this.hasCollapsedHeaderTarget) {
      this.collapsedHeaderTarget.classList.toggle(
        "hidden",
        !this.collapsedValue,
      )
    }
    if (this.hasExpandedHeaderTarget) {
      this.expandedHeaderTarget.classList.toggle("hidden", this.collapsedValue)
    }
    if (this.hasSearchBlockTarget) {
      this.searchBlockTarget.classList.toggle("hidden", this.collapsedValue)
    }
    if (this.hasListTarget) {
      this.listTarget.classList.toggle("hidden", this.collapsedValue)
    }
    // CONEK-4760 — the expanded content is an absolutely-positioned overlay
    // (see chat_list_rail_component.html.erb). Show it only when expanded.
    if (this.hasOverlayTarget) {
      this.overlayTarget.classList.toggle("hidden", this.collapsedValue)
    }
    // The scrim tracks the overlay exactly: it exists to show that the dimmed
    // sliver of conversation beside the drawer is a dismiss target.
    if (this.hasScrimTarget) {
      this.scrimTarget.classList.toggle("hidden", this.collapsedValue)
    }
    // The root <aside> stays pinned to the collapsed width as a flex spacer
    // in every state: when expanded, the overlay floats on top instead of
    // widening the rail, so the conversation pane keeps its position and
    // width (CONEK-4760). The expanded width lives on the overlay markup.
    if (this.hasRootTarget) {
      this.rootTarget.style.width = `${this.collapsedWidthValue}px`
    }
  }

  // ──────────────────────────────────────────────────────────────────────
  // Search

  // Workstream 3 sub-phase 3 — server-side search. Was client-side
  // row-hiding over the already-rendered 50-row cap, so a match OUTSIDE
  // that window was unreachable no matter what you typed. Debounced
  // 300ms, then round-trips through the SAME turbo-frame reload the
  // Active/Archived filter already uses (#reloadFrame), carrying
  // `companion_search` — CompanionLayoutHelper#companion_chats searches
  // the full active/archived scope server-side.
  //
  // A frame reload replaces the search input's DOM node, which would
  // normally drop focus mid-search — #restoreSearchFocus below puts focus
  // (and the caret, at the end of the value) back on the fresh node once
  // Turbo finishes rendering the frame.
  handleSearch(event) {
    const query = event.target.value
    if (this.searchTimeoutId) clearTimeout(this.searchTimeoutId)
    this.searchTimeoutId = setTimeout(() => {
      this.searchTimeoutId = null
      this.searchValue = query
      this.dispatch("search", { detail: { query } })
      this.#reloadFrame({ restoreSearchFocus: true })
    }, 300)
  }

  #restoreSearchFocus() {
    if (!this.hasSearchInputTarget) return
    const input = this.searchInputTarget
    input.focus()
    const end = input.value.length
    input.setSelectionRange(end, end)
  }

  // ──────────────────────────────────────────────────────────────────────
  // Status filter
  //
  // Active vs archived swaps the underlying dataset (kept vs discarded),
  // which the active view doesn't have loaded — so this needs a server
  // round-trip. Refresh the frame with the companion_status param; the
  // layout helper reads it and re-renders the rail filtered.

  filterActive() {
    if (this.statusValue === "active") return
    this.statusValue = "active"
    this.dispatch("filter", { detail: { status: "active" } })
    this.#reloadFrame()
  }

  filterArchived() {
    if (this.statusValue === "archived") return
    this.statusValue = "archived"
    this.dispatch("filter", { detail: { status: "archived" } })
    this.#reloadFrame()
  }

  // ──────────────────────────────────────────────────────────────────────
  // Row + new-chat actions

  newChat() {
    // Clear the persisted current-chat cookie so the next render starts
    // a fresh thread, then refresh the rail frame (the AIChatChannel
    // mints a new AIChat on subscribe — companion controller listens
    // for the dispatched event to re-subscribe).
    document.cookie =
      "companion_current_chat_id=; path=/; SameSite=Lax; max-age=0"
    this.dispatch("new-chat", { detail: {} })
    // The drawer is full-bleed over the conversation pane now, so leaving it
    // open after this action means the fresh thread happens invisibly behind
    // it — "New chat does nothing". Collapse to reveal the result.
    this.collapse()
    this.#reloadFrame()
  }

  selectChat(event) {
    const chatId = event.params?.chatId
    if (!chatId) return
    // Persist the selection so the server re-renders the rail (with
    // the new active highlight) on frame refresh. The dispatched
    // `select-chat` event is the signal the companion controller
    // listens for to re-subscribe the AIChatChannel to the new
    // ai_chat_id without a full page reload.
    document.cookie = `companion_current_chat_id=${chatId}; path=/; SameSite=Lax`
    this.dispatch("select-chat", { detail: { chatId } })
    // Same full-bleed rationale as newChat(): reveal the selected thread.
    this.collapse()
    // Move the active highlight in place instead of reloading the frame.
    // A frame reload re-renders the list and resets its scroll position
    // to the top — jarring when the user clicked a chat further down.
    // The companion controller loads the selected chat's messages off
    // the dispatched event; the cookie keeps the highlight correct on
    // the next full render.
    this.#setActiveRow(chatId)
  }

  // Toggle the active-row styling client-side (no re-render). Mirrors the
  // server-rendered classes in chat_list_rail_component.html.erb.
  #setActiveRow(chatId) {
    const rows = this.element.querySelectorAll(
      '[data-testid="chat-list-rail-row"]',
    )
    for (const row of rows) {
      const isActive = row.dataset.chatId === chatId
      row.dataset.active = String(isActive)
      row.classList.toggle("border-primary", isActive)
      row.classList.toggle("bg-primary/10", isActive)
      row.classList.toggle("border-transparent", !isActive)
      row.classList.toggle("hover:bg-muted/60", !isActive)
    }
  }

  // The row action buttons sit inside the row whose own click handler is
  // `selectChat`. Without stopping the row-click here, archiving or deleting
  // would also switch to that chat first. Bound on the actions wrapper.
  stopRowClick(event) {
    event.stopPropagation()
  }

  // "Rename" entry point. Mirrors React ChatListRail's inline rename UX: swap
  // the select <button> (name <p> lives inside it) for the prefilled <input>
  // sibling and focus + select it. The actual PATCH happens in renameKeydown /
  // renameBlur once committed.
  renameChat(event) {
    event.stopPropagation()
    const chatId = event.params?.chatId
    if (!chatId) return
    const nameEl = this.chatNameTargets.find(
      (el) => el.dataset.chatId === chatId,
    )
    const inputEl = this.renameInputTargets.find(
      (el) => el.dataset.chatId === chatId,
    )
    if (!nameEl || !inputEl) return
    // The button, not just the inner <p> — the <p> alone hiding would leave
    // an empty (but still focusable/clickable) button visible next to the
    // rename input.
    const selectButton = nameEl.closest("button")
    inputEl.dataset.originalValue = nameEl.textContent.trim()
    inputEl.value = nameEl.textContent.trim()
    selectButton?.classList.add("hidden")
    inputEl.classList.remove("hidden")
    // setTimeout so focus runs after the click event finishes — without it,
    // the click on the rename button steals focus straight back.
    setTimeout(() => {
      inputEl.focus()
      inputEl.select()
    }, 0)
  }

  // Enter commits, Escape cancels. Stops propagation so the row click
  // handler doesn't fire when the user is editing.
  async renameKeydown(event) {
    event.stopPropagation()
    if (event.key === "Enter") {
      event.preventDefault()
      await this.#commitRename(event.currentTarget)
    } else if (event.key === "Escape") {
      this.#cancelRename(event.currentTarget)
    }
  }

  // Blur commits when the value actually changed; an unchanged blur just
  // restores the label without a network round-trip. The committing flag
  // prevents a double-submit when Enter also fires blur.
  async renameBlur(event) {
    const input = event.currentTarget
    if (input.dataset.committing === "true") return
    const original = input.dataset.originalValue ?? ""
    if (input.value.trim() === original.trim()) {
      this.#cancelRename(input)
      return
    }
    await this.#commitRename(input)
  }

  async #commitRename(input) {
    const chatId = input.dataset.chatId
    const newName = input.value.trim()
    if (!chatId) return
    if (!newName) {
      this.#cancelRename(input)
      return
    }
    input.dataset.committing = "true"
    const ok = await this.#fetchJson(this.#chatUrl(chatId), "PATCH", {
      ai_chat: { name: newName },
    })
    if (ok) {
      this.#reloadFrame()
    } else {
      // PATCH failed — revert the swap so the user can retry without
      // losing their typed value.
      delete input.dataset.committing
      this.#cancelRename(input)
    }
  }

  #cancelRename(input) {
    const chatId = input.dataset.chatId
    const nameEl = this.chatNameTargets.find(
      (el) => el.dataset.chatId === chatId,
    )
    input.value = input.dataset.originalValue ?? input.value
    input.classList.add("hidden")
    nameEl?.closest("button")?.classList.remove("hidden")
  }

  async archiveChat(event) {
    event.stopPropagation()
    const chatId = event.params?.chatId
    if (!chatId) return
    // Status comes from the controller's persisted statusValue; "active"
    // means clicking "Archive" archives, "archived" means it unarchives.
    const path =
      this.statusValue === "archived"
        ? `${this.#chatUrl(chatId)}/unarchive`
        : `${this.#chatUrl(chatId)}/archive`
    const ok = await this.#fetchJson(path, "POST")
    if (ok) this.#reloadFrame()
  }

  // "Delete" entry point. Mirrors React ChatListRail: instead of
  // window.confirm, open the in-app modal and wait for the user to
  // confirm/cancel. We stash the chat-id on the confirm button so the
  // dialog stays a singleton (one modal at the rail root, no per-row
  // copies).
  deleteChat(event) {
    event.stopPropagation()
    const chatId = event.params?.chatId
    if (!chatId) return
    if (!this.hasDeleteDialogTarget || !this.hasDeleteConfirmBtnTarget) return
    this.deleteConfirmBtnTarget.dataset.chatId = chatId
    this.#openDeleteDialog()
  }

  deleteCancel(event) {
    event?.stopPropagation()
    this.#closeDeleteDialog()
  }

  async deleteConfirm(event) {
    event?.stopPropagation()
    const chatId = this.deleteConfirmBtnTarget?.dataset.chatId
    if (!chatId) {
      this.#closeDeleteDialog()
      return
    }
    const ok = await this.#fetchJson(this.#chatUrl(chatId), "DELETE")
    if (!ok) {
      this.#closeDeleteDialog()
      return
    }
    // If we deleted the currently-active chat, clear the cookie so the
    // next reload mints a fresh one instead of 404-looping, AND tell the
    // companion to drop the now-orphaned conversation. Without the
    // dispatch the messages pane keeps rendering the deleted chat's
    // history (it lives outside the rail's turbo-frame).
    const active = document.cookie.match(
      /(?:^|; )companion_current_chat_id=([^;]+)/,
    )
    if (active?.[1] === chatId) {
      document.cookie =
        "companion_current_chat_id=; path=/; SameSite=Lax; max-age=0"
      this.dispatch("new-chat", { detail: {} })
    }
    this.#reloadFrame()
  }

  // Defer open/close to the shared Conek Ui::ModalComponent's Stimulus
  // controller (`ui--modal`) so we inherit its focus trap, backdrop,
  // Esc-to-close and brand styling. We just grab the controller off the
  // dialog element and call its public methods.
  #modalController() {
    if (!this.hasDeleteDialogTarget) return null
    const app = this.application
    return app?.getControllerForElementAndIdentifier(
      this.deleteDialogTarget,
      "ui--modal",
    )
  }

  #openDeleteDialog() {
    this.#modalController()?.open()
  }

  #closeDeleteDialog() {
    this.#modalController()?.close()
    if (this.hasDeleteConfirmBtnTarget) {
      delete this.deleteConfirmBtnTarget.dataset.chatId
    }
  }

  // Build the AIChat REST path keeping the current space prefix so the
  // multi-tenant ActsAsTenant middleware resolves correctly. The rail
  // is now mounted on every authenticated layout (support layout, admin
  // chat page, etc.), so the previous "strip /admin/..." trick broke
  // outside /admin: on /s:demo:all/support/tickets it produced
  // /s:demo:all/support/tickets/admin/ai/chat/<id>. Extract just the
  // /s:<group>:<branches> prefix and re-append the canonical chat path.
  // Serialize a nested JS object into URLSearchParams using Rails' bracket
  // notation, e.g. {ai_chat: {name: "x"}} -> "ai_chat%5Bname%5D=x".
  // Mirrors what Rails' integration tests send when they pass `params:`
  // hashes, so the controller can `params.fetch(:ai_chat).permit(:name)`
  // without changes.
  #encodeFormParams(obj, prefix = "", params = new URLSearchParams()) {
    for (const [k, v] of Object.entries(obj)) {
      const key = prefix ? `${prefix}[${k}]` : k
      if (v === null || v === undefined) continue
      if (typeof v === "object" && !Array.isArray(v)) {
        this.#encodeFormParams(v, key, params)
      } else if (Array.isArray(v)) {
        for (const item of v) params.append(`${key}[]`, item)
      } else {
        params.append(key, v)
      }
    }
    return params
  }

  // Refresh just the rail by re-fetching the host page and letting
  // Turbo swap the matching <turbo-frame id="chat-list-rail">. Beats
  // window.location.reload(): preserves Stimulus/React state outside
  // the rail (e.g. the messages pane, ticket pane), avoids the
  // full-page flash and keeps the AIChatChannel subscription alive.
  #reloadFrame({ restoreSearchFocus = false } = {}) {
    const frame = document.getElementById("chat-list-rail")
    if (!frame) return
    // Workstream 3 sub-phase 7 review fix — reload against a dedicated
    // layout-independent endpoint, NOT the current page. Reloading
    // window.location.href relied on the CURRENT page's own layout
    // rendering the frame; turbo-rails' built-in Turbo-Frame layout swap
    // (Turbo::Frames::FrameRequest, present on every controller) replaces
    // that layout with a bare one that never contains SidebarComponent
    // (mounted at the layout level, not by any individual controller's
    // view) — Turbo then renders "Content missing". See
    // CompanionChatListRailController for the full explanation.
    const spacePrefix = window.currentSpace?.groupKey
      ? `/s:${window.currentSpace.groupKey}:${window.currentSpace.branchKeys}`
      : ""
    const url = new URL(
      `${spacePrefix}/companion/chat_list_rail`,
      window.location.origin,
    )
    // Carry the current Active/Archived filter AND search query on the
    // frame URL so both survive every refresh (toggle, search, rename,
    // archive, delete, new chat). CompanionLayoutHelper#companion_chats
    // reads companion_search server-side (workstream 3 sub-phase 3).
    url.searchParams.delete("companion_status")
    if (this.statusValue && this.statusValue !== "active") {
      url.searchParams.set("companion_status", this.statusValue)
    }
    url.searchParams.delete("companion_search")
    if (this.searchValue) {
      url.searchParams.set("companion_search", this.searchValue)
    }
    // Carry the variant too, or the first rename/archive/delete on the
    // full-page chat swaps the docked column for a 48px overlay strip — the
    // endpoint has no other way to know which surface asked.
    url.searchParams.delete("variant")
    if (this.docked) url.searchParams.set("variant", "docked")
    // And the widened window, so a rename/archive after "Show more" doesn't
    // snap the list back to the first 50. Only when it differs from the
    // default — the server clamps anything at or below it anyway.
    url.searchParams.delete("companion_limit")
    if (this.limitValue > 50) {
      url.searchParams.set("companion_limit", String(this.limitValue))
    }
    const target = url.pathname + url.search

    if (restoreSearchFocus) {
      const handler = () => {
        frame.removeEventListener("turbo:frame-render", handler)
        this.#restoreSearchFocus()
      }
      frame.addEventListener("turbo:frame-render", handler)
    }

    // When the target URL is unchanged (same-status refresh) setting
    // `src` won't refire the fetch — prefer reload(). When it changes
    // (status toggle), set src so Turbo navigates the frame.
    if (
      frame.getAttribute("src") === target &&
      typeof frame.reload === "function"
    ) {
      frame.reload()
    } else {
      frame.setAttribute("src", target)
    }
  }

  #chatUrl(chatId) {
    const spacePrefix = window.location.pathname.match(/^\/s:[^/]+/)?.[0] ?? ""
    return `${spacePrefix}/admin/ai/chat/${chatId}`
  }

  async #fetchJson(url, method, body = null) {
    const csrf = document
      .querySelector('meta[name="csrf-token"]')
      ?.getAttribute("content")
    // CRITICAL: never send Content-Type: application/json to admin
    // endpoints. Rodauth's `json_request?` matches on that header and
    // flips the request into JSON-API mode, which expects a JWT Bearer
    // token. With session-cookie auth only, the request comes back 401
    // "Please login to continue" even though the user is logged in.
    // Encode the payload as nested form fields (`ai_chat[name]=...`)
    // and let Rails' regular param parser pick it up — this is the same
    // shape the integration tests use.
    const headers = {
      Accept: "application/json",
      "X-Requested-With": "XMLHttpRequest",
      ...(csrf ? { "X-CSRF-Token": csrf } : {}),
      ...(body ? { "Content-Type": "application/x-www-form-urlencoded" } : {}),
    }
    try {
      const res = await fetch(url, {
        method,
        credentials: "same-origin",
        headers,
        body: body ? this.#encodeFormParams(body).toString() : undefined,
        // `keepalive` lets the browser finish the request even if the
        // page navigates immediately after dispatch. Critical for the
        // rename-on-blur flow: when the user blurs the input by clicking
        // a chat row, that row's selectChat handler reloads the page
        // before the PATCH would otherwise complete, and the change is
        // lost. Bodies are tiny (<1 KB) so the 64 KB keepalive cap is a
        // non-issue.
        keepalive: true,
      })
      return res.ok
    } catch (err) {
      console.warn("[ai--chat-list-rail] action failed:", err)
      return false
    }
  }
}
